import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "../supabaseHeroClient";
import { FiChevronLeft, FiChevronRight, FiX, FiImage, FiFilter } from "react-icons/fi";
import "./gallery.css";

const BUCKET_NAME = "Gallery-BB";

const EVENTS_LIST = [
  { id: "Global-Village-2025", name: "Global Village 2025", year: "2025", tag: "Festival" },
  { id: "Christmas-Bazaar-2024", name: "Christmas Craft Market", year: "2024", tag: "Craft Market" },
  { id: "Offroad-Experience", name: "BizBaz Offroad", year: "2024", tag: "Outdoor" },
  { id: "Spring-Festival-2024", name: "Spring Festival", year: "2024", tag: "Festival" },
];

const Gallery = () => {
  const [selectedFolder, setSelectedFolder] = useState(EVENTS_LIST[0].id);
  const [images, setImages] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadedThumbnails, setLoadedThumbnails] = useState({});

  // Clean public URL helper without Supabase transformations
  const getPublicUrl = (fileName, folder) => {
    const encodedName = encodeURIComponent(fileName);
    const path = `${folder}/${encodedName}`;
    const { data } = supabase.storage.from(BUCKET_NAME).getPublicUrl(path);
    return data.publicUrl;
  };

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setImages([]);
    setLoadedThumbnails({});

    const fetchFolderImages = async () => {
      const { data, error } = await supabase.storage
        .from(BUCKET_NAME)
        .list(selectedFolder, {
          limit: 100,
          sortBy: { column: "name", order: "asc" },
        });

      if (error) {
        console.error("❌ Supabase list error:", error);
        if (isMounted) setLoading(false);
        return;
      }

      if (!data || data.length === 0) {
        if (isMounted) {
          setImages([]);
          setLoading(false);
        }
        return;
      }

      const imageFiles = data.filter((file) =>
        /\.(jpg|jpeg|png|webp|gif)$/i.test(file.name)
      );

      const parsedImages = imageFiles.map((file) => {
        const url = getPublicUrl(file.name, selectedFolder);
        return {
          name: file.name,
          url: url,
        };
      });

      if (isMounted) {
        setImages(parsedImages);
        setLoading(false);
      }
    };

    fetchFolderImages();

    return () => {
      isMounted = false;
    };
  }, [selectedFolder]);

  /* Lightbox Navigation */
  const closeLightbox = () => setActiveIndex(null);

  const nextImage = useCallback(() => {
    if (images.length === 0) return;
    setActiveIndex((prev) => (prev === null ? 0 : (prev + 1) % images.length));
  }, [images.length]);

  const prevImage = useCallback(() => {
    if (images.length === 0) return;
    setActiveIndex((prev) =>
      prev === null ? 0 : (prev - 1 + images.length) % images.length
    );
  }, [images.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeIndex === null) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeIndex, nextImage, prevImage]);

  const activeEvent = EVENTS_LIST.find((e) => e.id === selectedFolder);

  return (
    <div className="gallery-page">
      {/* Header Banner */}
      <div className="gallery-header">
        <h1 className="gallery-title">EVENT GALLERY</h1>
        <p className="gallery-subtitle">
          Relive moments from our vibrant bazaars, cultural festivals, and live experiences across Nairobi.
        </p>
      </div>

      {/* Event Selection Tabs */}
      <div className="event-selector-container">
        <div className="event-selector-header">
          <FiFilter className="filter-icon" />
          <span>SELECT EVENT</span>
        </div>

        <div className="event-tabs">
          {EVENTS_LIST.map((event) => (
            <button
              key={event.id}
              className={`event-tab ${selectedFolder === event.id ? "active" : ""}`}
              onClick={() => setSelectedFolder(event.id)}
            >
              <span className="event-name">{event.name}</span>
              <span className="event-badge">{event.year}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Gallery Grid */}
      <section className="gallery-content">
        <div className="active-event-bar">
          <div className="meta-info">
            <h2>{activeEvent?.name}</h2>
            <span className="photo-count">
              <FiImage /> {images.length} Photos
            </span>
          </div>
        </div>

        {loading ? (
          <div className="gallery-grid">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="skeleton-card" />
            ))}
          </div>
        ) : images.length === 0 ? (
          <div className="empty-gallery">
            <FiImage className="empty-icon" />
            <h3>No Photos Found</h3>
            <p>We haven't uploaded photos for this event yet.</p>
          </div>
        ) : (
          <div className="gallery-grid">
            {images.map((img, index) => (
              <div
                key={img.name}
                className="gallery-card"
                onClick={() => setActiveIndex(index)}
              >
                {!loadedThumbnails[index] && <div className="skeleton-card absolute-skeleton" />}
                <img
                  src={img.url}
                  alt={`${activeEvent?.name} photo ${index + 1}`}
                  loading="lazy"
                  className={`gallery-thumb ${loadedThumbnails[index] ? "is-loaded" : ""}`}
                  onLoad={() =>
                    setLoadedThumbnails((prev) => ({ ...prev, [index]: true }))
                  }
                />
                <div className="card-overlay">
                  <span className="view-btn">View Full Size</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Lightbox Modal */}
      {activeIndex !== null && images[activeIndex] && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <button className="lightbox-close" onClick={closeLightbox} aria-label="Close">
            <FiX />
          </button>

          <button
            className="lightbox-nav prev"
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
            aria-label="Previous"
          >
            <FiChevronLeft />
          </button>

          <div className="lightbox-content-wrapper" onClick={(e) => e.stopPropagation()}>
            <img
              src={images[activeIndex].url}
              alt="Full size view"
              className="lightbox-raw-image"
            />
            <div className="lightbox-caption">
              {activeEvent?.name} — Photo {activeIndex + 1} of {images.length}
            </div>
          </div>

          <button
            className="lightbox-nav next"
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            aria-label="Next"
          >
            <FiChevronRight />
          </button>
        </div>
      )}
    </div>
  );
};

export default Gallery;